import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import { CONTROL, PRES, TYPE } from './chrome';
import { COPY, pageFormatLabel, slideCountText } from './presentationCopy';
import { createTemplate, listTemplates, patchTemplate } from './api';
import { PresentationTemplate } from './types';
import { focusVisibleSx } from './ui/focus';
import { SlideThumbnail } from './ui';
import SettingsGearButton from './SettingsGearButton';
import { layoutPreviewSlide, previewLayout } from './editor/templateLayoutPreview';

interface TemplatesHomeProps {
  onBack: () => void;
  onOpen: (templateId: string, layoutId?: string) => void;
  onOpenSettings?: () => void;
}

const TemplatesHome: React.FC<TemplatesHomeProps> = ({ onBack, onOpen, onOpenSettings }) => {
  const [templates, setTemplates] = useState<PresentationTemplate[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [renameId, setRenameId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  const reload = async () => {
    try {
      setTemplates(await listTemplates({ includeArchived: true }));
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || COPY.loadFailed);
    }
  };

  useEffect(() => { void reload(); }, []);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: PRES.surfaceSunken, fontFamily: TYPE.ui }}>
      <Box sx={{
        height: 56,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 2,
        bgcolor: PRES.surface,
        borderBottom: `1px solid ${PRES.borderSubtle}`,
        flexShrink: 0,
      }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          sx={{
            textTransform: 'none',
            color: PRES.interactive,
            fontWeight: 650,
            ...focusVisibleSx,
          }}
        >
          {COPY.allPresentations}
        </Button>
        <Box sx={{ width: '1px', alignSelf: 'stretch', my: 1.5, bgcolor: PRES.borderStrong, flexShrink: 0 }} />
        <Typography sx={{ color: PRES.textPrimary, fontFamily: TYPE.ui, ...TYPE.title }}>{COPY.templates}</Typography>
        <Box sx={{ flex: 1 }} />
        {onOpenSettings && <SettingsGearButton onClick={onOpenSettings} />}
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => { setName(''); setCreateOpen(true); }}
          sx={{ bgcolor: PRES.interactive, textTransform: 'none', boxShadow: 'none' }}
        >
          {COPY.newTemplate}
        </Button>
      </Box>
      <Box sx={{ p: 3, overflow: 'auto' }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography sx={{ mb: 2, color: PRES.textSecondary, fontFamily: TYPE.ui, ...TYPE.body }}>
          {COPY.templatesHelp}
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 2 }}>
          {templates.map((template) => (
            <Box
              key={template._id}
              sx={{
                bgcolor: PRES.surface,
                border: `1px solid ${PRES.borderSubtle}`,
                borderRadius: `${CONTROL.radiusCard}px`,
                overflow: 'hidden',
                opacity: template.isActive === false ? 0.7 : 1,
              }}
            >
              <Box
                onClick={() => onOpen(template._id, template.layouts[0]?.layoutId)}
                sx={{ cursor: 'pointer' }}
              >
                <SlideThumbnail
                  slide={layoutPreviewSlide(template._id, previewLayout(template.layouts) || { layoutId: 'empty', objects: [] })}
                  pageFormat={template.pageFormat}
                />
              </Box>
              <Box sx={{ p: 2 }}>
              <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.title, color: PRES.textPrimary }}>
                {template.name}
              </Typography>
              <Typography sx={{ mt: 0.5, color: PRES.textSecondary, fontFamily: TYPE.ui, ...TYPE.caption }}>
                {pageFormatLabel(template.pageFormat)} · {template.isActive === false ? COPY.archivedTemplate : slideCountText(template.layouts.length)}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                <Button
                  size="small"
                  variant="contained"
                  sx={{ bgcolor: PRES.interactive, textTransform: 'none', boxShadow: 'none' }}
                  onClick={() => onOpen(template._id, template.layouts[0]?.layoutId)}
                >
                  {COPY.openTemplate}
                </Button>
                <Button
                  size="small"
                  sx={{ textTransform: 'none' }}
                  onClick={() => { setRenameId(template._id); setRenameValue(template.name); }}
                >
                  {COPY.renameTemplate}
                </Button>
                <Button
                  size="small"
                  sx={{ textTransform: 'none' }}
                  onClick={async () => {
                    if (template.isActive !== false && !window.confirm(COPY.confirmArchiveTemplate)) return;
                    try {
                      await patchTemplate(template._id, { isActive: template.isActive === false });
                      await reload();
                    } catch (err: any) {
                      setError(err?.response?.data?.message || COPY.saveError);
                    }
                  }}
                >
                  {template.isActive === false ? COPY.restoreTemplate : COPY.archiveTemplate}
                </Button>
              </Box>
              </Box>
            </Box>
          ))}
        </Box>
        {!templates.length && (
          <Typography sx={{ color: PRES.textSecondary, fontFamily: TYPE.ui, ...TYPE.body }}>{COPY.templatesEmpty}</Typography>
        )}
      </Box>
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{COPY.newTemplate}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label={COPY.templateName}
            placeholder={COPY.templateNamePlaceholder}
            value={name}
            onChange={(event) => setName(event.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} sx={{ textTransform: 'none' }}>{COPY.cancel}</Button>
          <Button
            disabled={!name.trim()}
            variant="contained"
            sx={{ bgcolor: PRES.interactive, textTransform: 'none', boxShadow: 'none' }}
            onClick={async () => {
              try {
                const created = await createTemplate({ name: name.trim() });
                setCreateOpen(false);
                onOpen(created._id, created.layouts[0]?.layoutId);
              } catch (err: any) {
                setError(err?.response?.data?.message || COPY.createFailed);
              }
            }}
          >
            {COPY.createAction}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={Boolean(renameId)} onClose={() => setRenameId(null)} fullWidth maxWidth="xs">
        <DialogTitle>{COPY.renameTemplate}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label={COPY.templateName}
            value={renameValue}
            onChange={(event) => setRenameValue(event.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameId(null)} sx={{ textTransform: 'none' }}>{COPY.cancel}</Button>
          <Button
            disabled={!renameValue.trim() || !renameId}
            variant="contained"
            sx={{ bgcolor: PRES.interactive, textTransform: 'none', boxShadow: 'none' }}
            onClick={async () => {
              if (!renameId) return;
              try {
                await patchTemplate(renameId, { name: renameValue.trim() });
                setRenameId(null);
                await reload();
              } catch (err: any) {
                setError(err?.response?.data?.message || COPY.saveError);
              }
            }}
          >
            {COPY.confirm}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TemplatesHome;
